if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
/* hh-bandana-product-ui-guard-start */
/* hh-bandana-product-ui-cache 2026-06-20T00:23:06.520Z */
(function () {
  var path = "/products/adjustable-leather-strap-dog-bandana-with-gold-hardware-interchangeable-prints";
  var scheduled = false;
  var observer = null;
  var observedRoot = null;
  var message = "Please enter the personalization details before adding this item to your cart.";

  function clean(value) {
    return String(value || "").replace(/\s+/g, " ").trim();
  }

  function isBandanaPage() {
    return window.location.pathname === path || Boolean(document.querySelector('product-info[data-url="' + path + '"]'));
  }

  function bandanaRoot() {
    return document.querySelector('product-info[data-url="' + path + '"]') ||
      (window.location.pathname === path ? document.querySelector("product-info") : null);
  }

  if (!isBandanaPage()) return;
  if (window.__hhBandanaProductUiGuard) return;
  window.__hhBandanaProductUiGuard = true;

  function cssEscape(value) {
    return window.CSS && window.CSS.escape ? window.CSS.escape(value) : String(value).replace(/"/g, '\\\"');
  }

  function isVisible(element) {
    if (!element || element.disabled) return false;
    if (element.type === "hidden") return false;
    if (element.hidden || element.getAttribute("aria-hidden") === "true") return false;
    var style = window.getComputedStyle(element);
    if (style.display === "none" || style.visibility === "hidden") return false;
    return Boolean(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
  }

  function fieldText(element) {
    var parts = [
      element.name,
      element.id,
      element.getAttribute("aria-label"),
      element.getAttribute("placeholder"),
      element.getAttribute("data-name"),
      element.getAttribute("data-option-name")
    ];
    if (element.id) {
      var label = document.querySelector('label[for="' + cssEscape(element.id) + '"]');
      if (label) parts.push(label.textContent);
    }
    var wrapper = element.closest("label, fieldset, .field, .product-form__input, .selector-wrapper, .tpo_option-container, .easify-option, .easify-option-field, .epo-option, .epo-option-wrap, .po-option, [data-option-name], [data-label]");
    if (wrapper) parts.push(wrapper.textContent);
    return clean(parts.join(" "));
  }

  function hasVisibleControl(element) {
    return Boolean(element && element.querySelector && element.querySelector("select, input:not([type='hidden']), textarea, button, [role='combobox'], [role='listbox']"));
  }

  function groupFor(element) {
    var group = element.closest(".tpo_option-container, .easify-option, .easify-option-field, .epo-option, .epo-option-wrap, .po-option, fieldset, .product-form__input, .selector-wrapper, .field, [data-option-name], [data-option], [data-product-option]");
    if (group && hasVisibleControl(group)) return group;

    var current = element;
    for (var depth = 0; current && depth < 5; depth += 1) {
      if (current !== element && hasVisibleControl(current)) return current;
      current = current.parentElement;
    }
    return element.parentElement || element;
  }

  function hideStrapColor(root) {
    Array.prototype.slice.call(root.querySelectorAll("label, legend, .form__label, [data-label], .tpo_option-label, .tpo_option-label-container")).forEach(function (label) {
      var text = clean(label.textContent || label.getAttribute("data-label"));
      if (!/^strap\s*color$/i.test(text)) return;
      var group = groupFor(label);
      if (group) {
        group.hidden = true;
        group.setAttribute("aria-hidden", "true");
        group.style.setProperty("display", "none", "important");
      }
    });

    Array.prototype.slice.call(root.querySelectorAll("select, input, textarea, button, [role='combobox'], [role='listbox']")).forEach(function (control) {
      var text = fieldText(control);
      if (!/^strap\s*color$/i.test(text) && !/\bstrap\s*color\b/i.test(text)) return;
      var group = groupFor(control);
      if (group) {
        group.hidden = true;
        group.setAttribute("aria-hidden", "true");
        group.style.setProperty("display", "none", "important");
      }
      if ("disabled" in control) control.disabled = true;
      if (control.name) control.setAttribute("data-hh-disabled-name", control.name);
      control.removeAttribute("name");
    });
  }

  function personalizationControl(root) {
    var candidates = Array.prototype.slice.call(root.querySelectorAll("select, input[type='radio'], input[type='checkbox']")).filter(function (control) {
      var text = fieldText(control);
      return /\bpersonalization\b/i.test(text) && !/(details|text|name|message|line|note)/i.test(text);
    });
    return candidates.find(function (control) {
      if (control.matches("select")) return true;
      return control.checked || /\byes\b/i.test(control.value || fieldText(control));
    }) || null;
  }

  function personalizationSelected(root) {
    var select = Array.prototype.slice.call(root.querySelectorAll("select")).find(function (control) {
      var text = fieldText(control);
      return /\bpersonalization\b/i.test(text) && !/(details|text|name|message|line|note)/i.test(text);
    });
    if (select) {
      var selected = select.selectedOptions && select.selectedOptions[0];
      return /\byes\b/i.test(clean([select.value, selected && selected.textContent].join(" ")));
    }

    return Array.prototype.slice.call(root.querySelectorAll("input[type='radio'], input[type='checkbox']")).some(function (control) {
      return control.checked && /\bpersonalization\b/i.test(fieldText(control)) && /\byes\b/i.test(clean(control.value + " " + fieldText(control)));
    });
  }

  function detailControls(root) {
    return Array.prototype.slice.call(root.querySelectorAll("input, textarea")).filter(function (control) {
      if (!control.matches("input:not([type]), input[type='text'], input[type='search'], textarea")) return false;
      var text = fieldText(control);
      return /(personalization|personalize|engraving|engrave|name)/i.test(text) && /(details|text|name|message|line|note|personal)/i.test(text);
    });
  }

  function setDetailGroupVisible(control, visible) {
    var group = groupFor(control);
    if (!group) return;
    group.hidden = !visible;
    group.setAttribute("aria-hidden", visible ? "false" : "true");
    if (visible) {
      group.style.removeProperty("display");
    } else {
      group.style.setProperty("display", "none", "important");
    }
  }

  function sync() {
    scheduled = false;
    if (!isBandanaPage()) return;
    var root = bandanaRoot();
    if (!root) return;
    hideStrapColor(root);

    var selected = personalizationSelected(root);
    detailControls(root).forEach(function (control) {
      setDetailGroupVisible(control, selected);
      control.required = selected;
      control.toggleAttribute("required", selected);
      if (!selected || clean(control.value)) control.setCustomValidity("");
    });
  }

  function validate(root) {
    sync();
    if (!personalizationSelected(root)) return true;
    var blank = detailControls(root).find(function (control) {
      return isVisible(control) && !clean(control.value);
    });
    if (!blank) return true;
    blank.setCustomValidity(message);
    blank.reportValidity();
    blank.focus({ preventScroll: false });
    return false;
  }

  function schedule() {
    if (!isBandanaPage()) return;
    attachObserver();
    if (scheduled) return;
    scheduled = true;
    window.setTimeout(sync, 0);
  }

  function attachObserver() {
    var nextRoot = bandanaRoot();
    if (nextRoot === observedRoot) return;
    if (observer) observer.disconnect();
    observedRoot = nextRoot;
    if (!nextRoot || typeof MutationObserver !== "function") return;
    if (!observer) observer = new MutationObserver(schedule);
    observer.observe(nextRoot, { childList: true, subtree: true });
  }

  document.addEventListener("submit", function (event) {
    if (!isBandanaPage()) return;
    var form = event.target;
    if (!form || !form.matches || !form.matches("form[action*='/cart/add']")) return;
    var root = bandanaRoot();
    if (!root || !root.contains(form) || validate(root)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  document.addEventListener("click", function (event) {
    if (!isBandanaPage()) return;
    var button = event.target && event.target.closest && event.target.closest("button[type='submit'], input[type='submit'], [name='add']");
    if (!button) return;
    var form = button.form || button.closest("form");
    if (!form || !form.matches("form[action*='/cart/add']")) return;
    var root = bandanaRoot();
    if (!root || !root.contains(form) || validate(root)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  document.addEventListener("input", schedule, true);
  document.addEventListener("change", schedule, true);
  document.addEventListener("DOMContentLoaded", schedule);
  document.addEventListener("product-info:loaded", schedule);
  document.addEventListener("shopify:section:load", schedule);
  window.setTimeout(schedule, 150);
  window.setTimeout(schedule, 700);
  window.setTimeout(schedule, 1600);
  attachObserver();
})();
/* hh-bandana-product-ui-guard-end */
